import { useMemo, type ReactElement, type ReactNode } from 'react';
import { DecimalField, SelectField } from '@ff-it/form';
import { Box } from '@ff-it/ui';
import { toast } from 'react-toastify';
import type { ProductKind } from 'types';
import type { EmbeddedOutletOrServiceCategory } from '../../outletOrServiceCategory/types';
import type { ProductFormConfig } from '../../products/types';
import type { ProductAuthoringController } from './controller';
import type { ProductAuthoringProduct } from './types';
import {
  productFormInitialValues,
  productFormValuesFromProduct,
  type ProductFormValues,
} from '../../products/formValues';
import { ProductBaseFields } from '../../products/ProductBaseFields';
import { ProductFormShell } from '../../products/ProductFormShell';
import { toProductSaveCommand, type ProductAuthoringFormPayload } from './formSubmission';

export type ProductAuthoringFormValues = ProductAuthoringFormPayload;

type ProductAuthoringFormProps<TProduct extends ProductAuthoringProduct> = {
  controller: ProductAuthoringController<TProduct>;
  product?: TProduct;
  kind: ProductKind;
  /** Copy flow: seeded form values for the create branch. */
  seedValues?: ProductFormValues;
  formConfig?: ProductFormConfig;
  /** Whether this workspace owns the scalar price overlay. */
  listPriceEditable?: boolean;
  onClose: () => void;
  /** Catalogue actions rendered in the form footer (edit mode, live only). */
  footerActions?: ReactNode;
};

function OutletField({
  outlets,
  disabled,
}: {
  outlets: EmbeddedOutletOrServiceCategory[];
  disabled: boolean;
}): ReactElement {
  const options = useMemo(() => outlets.map((outlet) => ({ value: outlet, label: outlet.name })), [outlets]);
  return (
    <SelectField
      name="outlet_or_service_category"
      label="Outlet or service category"
      options={options}
      required
      disabled={disabled}
      format={(value: EmbeddedOutletOrServiceCategory | null) =>
        options.find((option) => option.value.id === value?.id) ?? null
      }
      parse={(option: { value: EmbeddedOutletOrServiceCategory } | null) => option?.value ?? null}
    />
  );
}

function initialValues<TProduct extends ProductAuthoringProduct>(
  controller: ProductAuthoringController<TProduct>,
  product: TProduct | undefined,
  kind: ProductKind,
  seedValues?: ProductFormValues,
): ProductAuthoringFormValues {
  const listPrice = product ? (controller.input.prices[product.id] ?? null) : null;
  if (product) {
    const { id: _id, ...productValues } = product;
    return {
      ...productFormValuesFromProduct(productValues),
      outlet_or_service_category: product.outlet_or_service_category,
      kind: product.kind,
      type: product.type,
      listPrice,
    };
  }

  return {
    ...productFormInitialValues(controller.input.priceList.type),
    ...seedValues,
    kind,
    type: controller.input.priceList.type,
    outlet_or_service_category: null,
    listPrice,
  };
}

function validate(values: ProductAuthoringFormValues): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!values.outlet_or_service_category) errors.outlet_or_service_category = 'Select an outlet or service category';
  if (values.kind === 'SMART') {
    if (!values.direction) errors.direction = 'This field is required';
    if (!values.application) errors.application = 'This field is required';
  }
  return errors;
}

export function ProductAuthoringForm<TProduct extends ProductAuthoringProduct>({
  controller,
  product,
  kind,
  seedValues,
  formConfig,
  listPriceEditable = true,
  onClose,
  footerActions,
}: ProductAuthoringFormProps<TProduct>): ReactElement {
  const creating = product === undefined;
  const values = initialValues(controller, product, kind, seedValues);
  const type = values.type;
  if (!type) throw new Error('Product authoring requires an activity type');

  return (
    <Box>
      <ProductFormShell
        initialValues={values}
        validate={validate}
        resetKey={product?.id ?? `create-${kind}`}
        creating={creating}
        onClose={onClose}
        footerActions={footerActions}
        onSubmit={async (formValues) => {
          const command = toProductSaveCommand<TProduct>(formValues, product?.id, listPriceEditable);
          if (!command) return;
          await controller.actions.saveProduct(command);
          toast.success(creating ? 'Item has been created.' : 'Item has been updated.');
          onClose();
        }}
      >
        <OutletField disabled={!creating} outlets={controller.input.outlets} />
        {listPriceEditable && (
          <DecimalField name="listPrice" label="List price" precision={16} help="Optional price on this price list" />
        )}
        <ProductBaseFields
          kind={kind}
          type={type}
          vendorId={controller.input.priceList.vendor_id}
          config={formConfig}
          showServiceGrossPrice
        />
      </ProductFormShell>
    </Box>
  );
}
